import React, { useCallback, useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { format } from "date-fns";
import { publicApi } from "../../api/http";
import {
  authAxios,
  authRequestHeaders,
  useAuth,
} from "../../contexts/AuthContext";
import PageLoader from "../../components/PageLoader/PageLoader";
import RequireAuth from "../../components/RequireAuth/RequireAuth";
import { userDisplayName, userInitial } from "../../utils/userDisplayName";
import { isRankingPublished } from "../../utils/independentRanking";
import "./Profile.css";


const fieldSx = {
  "& .MuiOutlinedInput-root": {
    color: "var(--text-primary)",
    borderRadius: 2,
    "& fieldset": { borderColor: "#2d3344" },
    "&:hover fieldset": { borderColor: "#4a5568" },
    "&.Mui-focused fieldset": { borderColor: "var(--brand-strong)" },
  },
  "& .MuiInputLabel-root": { color: "var(--text-muted)" },
  "& .MuiInputLabel-root.Mui-focused": { color: "var(--brand-strong)" },
};

function ProfileRankingsList({ rankings, isOwner }) {
  if (!rankings.length) {
    return (
      <Typography sx={{ color: "var(--text-muted)" }}>
        {isOwner
          ? "No rankings yet. "
          : "No published rankings yet."}
        {isOwner && (
          <Link to="/your-rankings/create" className="link-default">
            Create one
          </Link>
        )}
      </Typography>
    );
  }

  return (
    <div className="profile-rankings-list">
      {rankings.map((r) => {
        const published = isRankingPublished(r);
        const isDraft = Boolean(r.draft);
        const viewPath = `/independent-rankings/${r.independent_ranking_id}`;
        const editPath = `/your-rankings/${r.independent_ranking_id}/edit`;

        return (
          <article
            key={r.independent_ranking_id}
            className={[
              "profile-ranking-card",
              isDraft ? "profile-ranking-card--draft" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <div className="profile-ranking-card-main">
              {isDraft && isOwner && (
                <Chip
                  label="Draft"
                  size="small"
                  sx={{
                    mb: 0.5,
                    height: 20,
                    fontSize: "0.65rem",
                    bgcolor: "var(--brand-soft)",
                    color: "var(--brand-strong)",
                  }}
                />
              )}
              <Link to={viewPath} className="profile-ranking-title">
                {r.title}
              </Link>
              <span className="profile-ranking-caption">
                Draft {r.draft_year}
                {r.creation_date &&
                  ` · ${format(new Date(r.creation_date), "MMM d, yyyy")}`}
              </span>
            </div>
            <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
              <Button
                component={Link}
                to={viewPath}
                variant="outlined"
                size="small"
                sx={{ textTransform: "none" }}
              >
                View
              </Button>
              {isOwner && !published && (
                <Button
                  component={Link}
                  to={editPath}
                  variant="contained"
                  size="small"
                  disableElevation
                  sx={{
                    textTransform: "none",
                    color: "#0b0d10",
                    background: "linear-gradient(145deg, var(--brand-strong), var(--brand))",
                  }}
                >
                  Edit
                </Button>
              )}
            </Box>
          </article>
        );
      })}
    </div>
  );
}

function ProfileContent() {
  const { user_id: routeUserId } = useParams();
  const { user, ensureAccessToken, updateUser } = useAuth();
  const isOwnProfileRoute = !routeUserId || routeUserId === "me";
  const viewingOwnProfile =
    isOwnProfileRoute || Boolean(user?.id && routeUserId === user.id);

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    bio: "",
    affiliation: "",
    picture_url: "",
  });

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setLoadError("");

    try {
      if (viewingOwnProfile) {
        const token = await ensureAccessToken();
        if (!token) {
          setLoadError("Session expired. Please sign in again.");
          return;
        }
        const res = await authAxios.get("/users/me", {
          headers: authRequestHeaders(token),
        });
        setProfile(res.data);
      } else {
        const token = await ensureAccessToken();
        const res = await publicApi.get(`/users/${routeUserId}`, {
          headers: token ? authRequestHeaders(token) : undefined,
        });
        setProfile(res.data);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setLoadError(
        typeof detail === "string" ? detail : "Could not load profile."
      );
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [viewingOwnProfile, routeUserId, ensureAccessToken]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    if (!user) {
      setEditing(false);
    }
  }, [user]);

  const syncFormFromProfile = useCallback((user) => {
    if (!user) return;
    setForm({
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      bio: user.bio || "",
      affiliation: user.affiliation || "",
      picture_url: user.picture_url || "",
    });
  }, []);

  const handleStartEdit = () => {
    if (!user || !profile?.is_owner) return;
    syncFormFromProfile(profile?.user);
    setSaveError("");
    setSaveSuccess(false);
    setEditing(true);
  };

  const handleCancelEdit = () => {
    syncFormFromProfile(profile?.user);
    setSaveError("");
    setSaveSuccess(false);
    setEditing(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!user || !profile?.is_owner) {
      setSaveError("You must be signed in to edit your profile.");
      return;
    }

    setSaving(true);
    setSaveError("");
    setSaveSuccess(false);

    try {
      const token = await ensureAccessToken();
      if (!token) {
        setSaveError("Session expired. Please sign in again.");
        setSaving(false);
        return;
      }

      const res = await authAxios.put(
        "/users/me",
        {
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          bio: form.bio.trim() || null,
          affiliation: form.affiliation.trim() || null,
          picture_url: form.picture_url.trim() || null,
        },
        { headers: authRequestHeaders(token) }
      );

      setProfile(res.data);
      updateUser(res.data.user);
      setSaveSuccess(true);
      setEditing(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setSaveError(
        typeof detail === "string" ? detail : "Failed to save profile."
      );
    } finally {
      setSaving(false);
    }
  };

  const displayUser = profile?.user;
  const isOwner = Boolean(profile?.is_owner);
  const canEdit = Boolean(user && isOwner);
  const pageTitle = displayUser
    ? `${userDisplayName(displayUser)} - Profile`
    : "Profile";

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        {profile && !isOwner && <meta name="robots" content="index" />}
        {isOwner && <meta name="robots" content="noindex" />}
      </Helmet>
      <PageLoader loading={loading} />
      <div className="profile-shell top-container">
        <Container className="container profile-page">
          <Grid container spacing={2}>
            {loadError && (
              <Grid item xs={12}>
                <Typography sx={{ color: "var(--negative)" }}>{loadError}</Typography>
              </Grid>
            )}

            {displayUser && (
              <>
                <Grid item xs={12}>
                  <header className="profile-header-wrap">
                    <div className="profile-top-bar">
                      <div className="profile-identity">
                        <Avatar
                          src={displayUser.picture_url || undefined}
                          alt={userDisplayName(displayUser)}
                          className="profile-avatar"
                          sx={{ bgcolor: "var(--brand)", width: 72, height: 72 }}
                        >
                          {!displayUser.picture_url && userInitial(displayUser)}
                        </Avatar>
                        <Typography component="h1" className="profile-name">
                          {userDisplayName(displayUser)}
                        </Typography>
                      </div>
                      {canEdit && !editing && (
                        <Button
                          type="button"
                          variant="contained"
                          onClick={handleStartEdit}
                          className="profile-save-btn profile-edit-btn"
                          disableElevation
                        >
                          Edit profile
                        </Button>
                      )}
                    </div>
                    <div className="profile-details">
                      {displayUser.email && canEdit && (
                        <p className="profile-meta-line">{displayUser.email}</p>
                      )}
                      {displayUser.affiliation && (
                        <p className="profile-affiliation">
                          {displayUser.affiliation}
                        </p>
                      )}
                      {displayUser.bio ? (
                        <p className="profile-bio">{displayUser.bio}</p>
                      ) : (
                        canEdit &&
                        !editing && (
                          <p className="profile-bio profile-bio--empty">
                            No bio yet.
                          </p>
                        )
                      )}
                      {displayUser.is_verified && (
                        <Chip
                          label="Verified"
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ mt: 1, height: 22, fontSize: "0.7rem" }}
                        />
                      )}
                      {saveSuccess && !editing && (
                        <Typography sx={{ color: "var(--brand-strong)", mt: 1.5 }}>
                          Profile saved.
                        </Typography>
                      )}
                    </div>
                  </header>
                </Grid>

                {canEdit && editing && (
                  <Grid item xs={12}>
                    <Typography component="h2" className="profile-section-title">
                      Edit profile
                    </Typography>
                    <Box
                      component="form"
                      onSubmit={handleSave}
                      className="profile-edit-form"
                    >
                      <TextField
                        fullWidth
                        required
                        label="First name"
                        value={form.first_name}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, first_name: e.target.value }))
                        }
                        margin="normal"
                        sx={fieldSx}
                      />
                      <TextField
                        fullWidth
                        required
                        label="Last name"
                        value={form.last_name}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, last_name: e.target.value }))
                        }
                        margin="normal"
                        sx={fieldSx}
                      />
                      <TextField
                        fullWidth
                        label="Affiliation"
                        placeholder="Team, publication, or organization"
                        value={form.affiliation}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, affiliation: e.target.value }))
                        }
                        margin="normal"
                        sx={fieldSx}
                      />
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label="Bio"
                        value={form.bio}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, bio: e.target.value }))
                        }
                        margin="normal"
                        sx={fieldSx}
                      />
                      <TextField
                        fullWidth
                        label="Profile picture URL"
                        value={form.picture_url}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, picture_url: e.target.value }))
                        }
                        margin="normal"
                        sx={fieldSx}
                      />
                      {saveError && (
                        <Typography sx={{ color: "var(--negative)", mt: 1 }}>
                          {saveError}
                        </Typography>
                      )}
                      <div className="profile-actions">
                        <Button
                          type="submit"
                          variant="contained"
                          disabled={saving}
                          className="profile-save-btn"
                          disableElevation
                        >
                          {saving ? "Saving…" : "Save changes"}
                        </Button>
                        <Button
                          type="button"
                          variant="outlined"
                          disabled={saving}
                          onClick={handleCancelEdit}
                          sx={{ textTransform: "none" }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <div className="profile-section-header">
                    <Typography component="h2" className="profile-section-title">
                      {isOwner ? "Your rankings" : "Published rankings"}
                    </Typography>
                    {canEdit && (
                      <Button
                        component={Link}
                        to="/your-rankings"
                        variant="outlined"
                        size="small"
                        sx={{ textTransform: "none", flexShrink: 0 }}
                      >
                        Manage rankings
                      </Button>
                    )}
                  </div>
                  <ProfileRankingsList
                    rankings={profile.rankings || []}
                    isOwner={isOwner}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Container>
      </div>
    </>
  );
}

function Profile() {
  const { user_id: routeUserId } = useParams();
  const { user } = useAuth();
  const isOwnRoute = !routeUserId || routeUserId === "me";
  const isOwnProfile =
    isOwnRoute || Boolean(user?.id && routeUserId === user.id);

  if (isOwnProfile) {
    return (
      <RequireAuth>
        <ProfileContent />
      </RequireAuth>
    );
  }

  return <ProfileContent />;
}

export default Profile;
